import React, { useState } from 'react';
import * as Card from '../../../v2BaseComponents/Card';
import getDateString from '../../../../utils/getDateString';
import Text from '../../../v2BaseComponents/Text';
import { getIntl } from '../../../../utils/intl';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import Icon from '../../../v2BaseComponents/Icon';
import { View } from 'react-native';
import PressableDynamicOpacity from '../../../v2BaseComponents/PressableDynamicOpacity';

const intl = getIntl('screens.mainApp.homebase.homebaseScreen.logList.BaseLogCard', {
    viewComments: 'View {count} Comment{plural}',
    showMore: '...show more',
    showLess: 'show less',
});

export type BaseLogCardOwnProps = {
    avatar: React.ReactNode;
    headerText: React.ReactNode | string;
    logMessage: string | null;
    timeStamp?: string | null;
    commentsCount?: number;
    onPress?: () => void;
    shouldEnableShowMore?: boolean;
    testID?: string;
    flat?: boolean;
};

export type BaseLogCardProps = BaseLogCardOwnProps;

const maxContentLength = 200;

// must be less than 8 explicit new lines and 200 characters
const clipMessage = (message: string): string => {
    const messageLines = message.split('\n');
    const clippedMessage = messageLines.slice(0, 8).join('\n');
    return clippedMessage.length > maxContentLength ? clippedMessage.slice(0, maxContentLength) : clippedMessage;
};

const BaseLogCard: React.FC<BaseLogCardProps> = ({
    avatar,
    logMessage,
    headerText,
    timeStamp,
    commentsCount,
    onPress,
    shouldEnableShowMore,
    testID,
    flat,
}) => {
    const displayShowMore = shouldEnableShowMore && logMessage && clipMessage(logMessage).length < logMessage.length;
    const [showMore, setShowMore] = useState(false);

    return (
        <Card.Card onPress={onPress} testID={testID} flat={flat}>
            <Card.CardLeftContent leftContent={avatar}>
                <Card.CardContent>
                    {typeof headerText === 'string' ? (
                        <Text variant="body-1" color="Dark-Text">
                            {headerText}
                        </Text>
                    ) : (
                        headerText
                    )}
                    {logMessage ? (
                        <Text variant="body-1" color="Dark-Text">
                            {showMore || !displayShowMore ? logMessage : `${clipMessage(logMessage)}...`}
                        </Text>
                    ) : null}
                    <Stack horizontal fullWidth align="center" justify="space-between">
                        {timeStamp ? (
                            <Card.CardCaption>{getDateString(timeStamp, 'date-time')}</Card.CardCaption>
                        ) : (
                            <View />
                        )}
                        <View>
                            {displayShowMore ? (
                                <PressableDynamicOpacity onPress={() => setShowMore(!showMore)}>
                                    <Card.CardCaption>
                                        {showMore ? intl('showLess') : intl('showMore')}
                                    </Card.CardCaption>
                                </PressableDynamicOpacity>
                            ) : null}
                        </View>
                    </Stack>
                    {commentsCount ? (
                        <Stack horizontal fullWidth justify="flex-start" align="center" space={contentStackSpacing}>
                            <Text variant="body-2" color="Dark-Text">
                                {intl('viewComments', {
                                    count: commentsCount,
                                    plural: commentsCount > 1 ? 's' : '',
                                })}
                            </Text>
                            <Icon name="MessageSquare" size="checkbox-icon" />
                        </Stack>
                    ) : null}
                </Card.CardContent>
            </Card.CardLeftContent>
        </Card.Card>
    );
};

export default React.memo(BaseLogCard);
